import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TableSortLabel,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { FileDownload } from '@mui/icons-material';
import { Workflow, SortConfig } from '../types';
import { exportWorkflowsToCSV, generateCSVFilename } from '../csvExport';
import { useWorkflowTypes } from '../hooks/useWorkflowTypes';
import { colors } from '../../../constants/globalcss';

interface WorkflowTableProps {
  workflows: Workflow[];
  title: string;
  tableType: 'my' | 'company';
  sortConfig: SortConfig | null;
  onSort: (key: keyof Workflow, table: 'my' | 'company') => void;
  onResponseClick: (workflowId: string) => void;
  totalCount?: number;
  isFiltered?: boolean;
  maxHeight: string | number;
}

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
  workflows,
  title,
  tableType,
  sortConfig,
  onSort,
  onResponseClick,
  maxHeight,
  totalCount,
  isFiltered = false,
}) => {
  const { workflowTypes } = useWorkflowTypes();

  if (workflows.length === 0) return null;

  const handleExportCSV = () => {
    const filename = generateCSVFilename(tableType);
    exportWorkflowsToCSV(workflows, filename, workflowTypes);
  };

  const renderCountChip = () => {
    const count = workflows.length;
    const total = totalCount || count;

    if (isFiltered && totalCount && totalCount > count) {
      return (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <Chip
            label={`${count} of ${total}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              '& .MuiChip-label': { px: 1.5 },
              backgroundColor: colors.background.paper,
              borderColor: colors.warning,
              color: colors.warning,
              fontWeight: 'bold',
            }}
          />
          <Typography
            variant="caption"
            color={colors.text.secondary}
            sx={{ fontSize: '0.75rem' }}
          >
            filtered
          </Typography>
        </Box>
      );
    } else {
      return (
        <Chip
          label={`${count} ${count === 1 ? 'item' : 'items'}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            '& .MuiChip-label': { px: 1.5 },
            backgroundColor: colors.background.paper,
            borderColor: colors.primary,
            color: colors.primary,
            fontWeight: 'bold',
          }}
        />
      );
    }
  };

  const getSortDirection = (key: keyof Workflow) => {
    return sortConfig?.key === key && sortConfig?.table === tableType
      ? sortConfig.direction
      : 'asc';
  };

  const isSortActive = (key: keyof Workflow) => {
    return sortConfig?.key === key && sortConfig?.table === tableType;
  };

  const SortableHeader: React.FC<{
    field: keyof Workflow;
    label: string;
    minWidth?: number;
  }> = ({ field, label, minWidth = 120 }) => (
    <TableCell
      sx={{
        backgroundColor: colors.primary,
        color: colors.background.paper,
        fontWeight: 'bold',
        maxWidth: minWidth,
        minWidth,
        width: minWidth,
      }}
    >
      <TableSortLabel
        active={isSortActive(field)}
        direction={getSortDirection(field)}
        onClick={() => onSort(field, tableType)}
        sx={{
          '& .MuiTableSortLabel-icon': {
            color: `${colors.background.paper} !important`,
          },
          '&.Mui-active': {
            color: `${colors.background.paper} !important`,
          },
          '&:hover': {
            color: `${colors.background.paper} !important`,
          },
          color: `${colors.background.paper} !important`,
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: maxHeight,
        mb: 2,
        minHeight: 'fit-content',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          flexShrink: 0,
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ flex: 1, textAlign: 'left' }}>
          {title}
        </Typography>
        {renderCountChip()}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownload />}
          onClick={handleExportCSV}
          sx={{
            '&:hover': {
              backgroundColor: colors.primary,
              borderColor: colors.primary,
              color: colors.background.paper,
            },
            backgroundColor: colors.background.paper,
            borderColor: colors.primary,
            borderRadius: 2,
            color: colors.primary,
            fontWeight: 500,
            ml: 1,
            textTransform: 'none',
          }}
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{
          '& .MuiTableHead-root': {
            backgroundColor: colors.background.paper,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          maxHeight: maxHeight,
          overflow: 'auto',
          width: '100%',
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ tableLayout: 'fixed', width: '100%' }}
        >
          <TableHead>
            <TableRow>
              <SortableHeader
                field="accountId"
                label="Servicer Account ID"
                minWidth={140}
              />
              <SortableHeader field="requestType" label="Type" minWidth={120} />
              <SortableHeader
                field="assignedToName"
                label="Assigned To"
                minWidth={150}
              />
              <SortableHeader
                field="assignedDate"
                label="Assigned Date"
                minWidth={120}
              />
              <TableCell
                sx={{
                  backgroundColor: colors.primary,
                  color: colors.background.paper,
                  maxWidth: 100,
                  minWidth: 100,
                  width: 100,
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows.map((workflow, index) => (
              <TableRow
                key={workflow.id}
                hover
                sx={{
                  backgroundColor:
                    index % 2 === 0
                      ? colors.background.paper
                      : 'rgba(245,245,245,0.7)', // much lighter than default
                  cursor: 'default',
                }}
              >
                <TableCell sx={{ maxWidth: 140, minWidth: 140, width: 140 }}>
                  <Typography variant="body2" noWrap>
                    {workflow.accountId}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 120, minWidth: 120, width: 120 }}>
                  <Typography variant="body2" noWrap>
                    {workflowTypes.find(
                      (wt) => wt.value === (workflow.requestType || '')
                    )?.label || workflow.requestType}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 150, minWidth: 150, width: 150 }}>
                  <Typography variant="body2" noWrap>
                    {workflow.assignedToName || workflow.assignedTo}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 120, minWidth: 120, width: 120 }}>
                  <Typography variant="body2" noWrap>
                    {(() => {
                      if (
                        workflow.assignedDate &&
                        workflow.assignedDate.includes('/')
                      ) {
                        const datePart = workflow.assignedDate.split(' ')[0];
                        return datePart;
                      }
                      const date = new Date(workflow.assignedDate);
                      return date.toLocaleDateString();
                    })()}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    minWidth: 100,
                    width: 100,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => onResponseClick(workflow.linkId)}
                    sx={{
                      '&:hover': {
                        backgroundColor: colors.primary,
                        borderColor: colors.primary,
                        color: colors.background.paper,
                      },
                      backgroundColor: colors.background.paper,
                      borderColor: colors.primary,
                      color: colors.primary,
                      fontSize: '0.75rem',
                      px: 1,
                    }}
                    startIcon={<EditIcon fontSize="small" />}
                  >
                    Respond
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
